import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'

type Props = {
  onBrandingUpdate?: (url: string | null) => void
}

export default function GlobalConfigPage({ onBrandingUpdate }: Props) {
  const [smartIndexing, setSmartIndexing] = useState(true)
  const [smartIndexingLoading, setSmartIndexingLoading] = useState(true)
  const [smartIndexingSaving, setSmartIndexingSaving] = useState(false)

  // Inference engine state
  const [inferenceEngine, setInferenceEngine] = useState<'ollama' | 'vllm'>('ollama')
  const [vllmBaseUrl, setVllmBaseUrl] = useState('')
  const [vllmModel, setVllmModel] = useState('')
  const [vllmApiKey, setVllmApiKey] = useState('')
  const [hasVllmApiKey, setHasVllmApiKey] = useState(false)
  const [engineSaving, setEngineSaving] = useState(false)
  const [engineSaved, setEngineSaved] = useState(false)

  useEffect(() => {
    api.get('/global-config').then(({ data }) => {
      setSmartIndexing(data.smart_indexing_enabled ?? true)
      setInferenceEngine(data.inference_engine ?? 'ollama')
      setVllmBaseUrl(data.vllm_base_url ?? '')
      setVllmModel(data.vllm_model ?? '')
      setHasVllmApiKey(data.has_vllm_api_key ?? false)
    }).catch(() => {}).finally(() => setSmartIndexingLoading(false))
  }, [])

  const toggleSmartIndexing = async (enabled: boolean) => {
    setSmartIndexingSaving(true)
    try {
      const { data } = await api.put('/global-config', { smart_indexing_enabled: enabled })
      setSmartIndexing(data.smart_indexing_enabled ?? enabled)
    } catch { /* keep current state */ }
    setSmartIndexingSaving(false)
  }

  const saveEngineConfig = async () => {
    setEngineSaving(true)
    setEngineSaved(false)
    try {
      const payload: Record<string, any> = {
        inference_engine: inferenceEngine,
        vllm_base_url: vllmBaseUrl || null,
        vllm_model: vllmModel || null,
      }
      if (vllmApiKey) payload.vllm_api_key = vllmApiKey
      const { data } = await api.put('/global-config', payload)
      setInferenceEngine(data.inference_engine ?? 'ollama')
      setVllmBaseUrl(data.vllm_base_url ?? '')
      setVllmModel(data.vllm_model ?? '')
      setHasVllmApiKey(data.has_vllm_api_key ?? false)
      setVllmApiKey('')
      setEngineSaved(true)
      setTimeout(() => setEngineSaved(false), 3000)
    } catch { /* keep current state */ }
    setEngineSaving(false)
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900">
      <div className="relative mx-auto max-w-[1400px] px-6 py-8">
        <div className="mb-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 bg-white/80 px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm transition hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/70 dark:text-zinc-300 dark:hover:bg-zinc-800"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-zinc-900 dark:text-white">Configuration Globale</h1>
          <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
            Gérez les paramètres globaux de la plateforme Totemium
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Tuile 1: Branding */}
          <Link
            to="/global-config/branding"
            className="group relative overflow-hidden rounded-2xl border border-zinc-200/70 bg-white p-6 shadow-lg transition hover:shadow-xl dark:border-zinc-800 dark:bg-zinc-900/90"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/30">
                <svg className="h-6 w-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900 dark:text-white">Branding</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Logo et identité visuelle</p>
              </div>
              <svg className="h-5 w-5 text-zinc-400 transition group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          {/* Tuile 2: SendGrid */}
          <Link
            to="/global-config/sendgrid"
            className="group relative overflow-hidden rounded-2xl border border-zinc-200/70 bg-white p-6 shadow-lg transition hover:shadow-xl dark:border-zinc-800 dark:bg-zinc-900/90"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30">
                <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900 dark:text-white">SendGrid Notifications</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Configuration des e-mails</p>
              </div>
              <svg className="h-5 w-5 text-zinc-400 transition group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          {/* Tuile 3: Agent Public */}
          <Link
            to="/global-config/public-agent"
            className="group relative overflow-hidden rounded-2xl border border-zinc-200/70 bg-white p-6 shadow-lg transition hover:shadow-xl dark:border-zinc-800 dark:bg-zinc-900/90"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/30">
                <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900 dark:text-white">Agent Public</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Configuration du widget public</p>
              </div>
              <svg className="h-5 w-5 text-zinc-400 transition group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          {/* Tuile 4: Widgets */}
          <Link
            to="/global-config/widget"
            className="group relative overflow-hidden rounded-2xl border border-emerald-200/70 bg-emerald-50 p-6 shadow-lg transition hover:shadow-xl dark:border-emerald-900/60 dark:bg-emerald-900/20"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/50">
                <svg className="h-6 w-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900 dark:text-white">Widgets</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Widgets public et interne</p>
              </div>
              <svg className="h-5 w-5 text-emerald-600 transition group-hover:translate-x-1 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          {/* Tuile 5: Prompts Système */}
          <Link
            to="/global-config/system-prompts"
            className="group relative overflow-hidden rounded-2xl border border-zinc-200/70 bg-white p-6 shadow-lg transition hover:shadow-xl dark:border-zinc-800 dark:bg-zinc-900/90"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/30">
                <svg className="h-6 w-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900 dark:text-white">Prompts Système</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Gestion des prompts</p>
              </div>
              <svg className="h-5 w-5 text-zinc-400 transition group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          {/* Tuile 6: Payment Stripe */}
          <Link
            to="/global-config/payment"
            className="group relative overflow-hidden rounded-2xl border border-zinc-200/70 bg-white p-6 shadow-lg transition hover:shadow-xl dark:border-zinc-800 dark:bg-zinc-900/90"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
                <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900 dark:text-white">Payment Stripe</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Configuration des paiements</p>
              </div>
              <svg className="h-5 w-5 text-zinc-400 transition group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>

          {/* Tuile 7: Ollama Cloud */}
          <Link
            to="/global-config/ollama"
            className="group relative overflow-hidden rounded-2xl border border-zinc-200/70 bg-white p-6 shadow-lg transition hover:shadow-xl dark:border-zinc-800 dark:bg-zinc-900/90"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-100 dark:bg-rose-900/30">
                <svg className="h-6 w-6 text-rose-600 dark:text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900 dark:text-white">Ollama Cloud</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">Configuration des modèles</p>
              </div>
              <svg className="h-5 w-5 text-zinc-400 transition group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </Link>
        </div>

        {/* Section: Moteur d'inférence */}
        <div className="mt-8 rounded-2xl border border-violet-200/70 bg-gradient-to-br from-violet-50/80 to-white/80 p-6 shadow-lg dark:border-violet-900/50 dark:from-violet-950/30 dark:to-zinc-950/80">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/40">
              <svg className="h-6 w-6 text-violet-600 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-zinc-900 dark:text-white">Moteur d'inférence LLM</h3>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                Basculez entre Ollama (llama.cpp, local/CPU) et vLLM (GPU dédié, haute performance) pour le chat et la segmentation IA.
              </p>

              {/* Engine selector */}
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setInferenceEngine('ollama')}
                  className={`flex items-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-medium transition ${
                    inferenceEngine === 'ollama'
                      ? 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-900/30 dark:text-violet-300'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:border-zinc-600'
                  }`}
                >
                  <span className="text-lg">🦙</span>
                  <div className="text-left">
                    <div>Ollama</div>
                    <div className="text-xs font-normal opacity-70">llama.cpp · CPU/GPU</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setInferenceEngine('vllm')}
                  className={`flex items-center gap-2 rounded-xl border-2 px-4 py-3 text-sm font-medium transition ${
                    inferenceEngine === 'vllm'
                      ? 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-900/30 dark:text-violet-300'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:border-zinc-600'
                  }`}
                >
                  <span className="text-lg">⚡</span>
                  <div className="text-left">
                    <div>vLLM</div>
                    <div className="text-xs font-normal opacity-70">GPU dédié · Haute perf</div>
                  </div>
                </button>
              </div>

              {/* vLLM config fields */}
              {inferenceEngine === 'vllm' && (
                <div className="mt-4 space-y-3 rounded-xl border border-violet-200/50 bg-white/60 p-4 dark:border-violet-800/30 dark:bg-zinc-900/50">
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400">URL du serveur vLLM</label>
                    <input
                      type="text"
                      value={vllmBaseUrl}
                      onChange={(e) => setVllmBaseUrl(e.target.value)}
                      placeholder="http://gpu-server:8000"
                      className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder-zinc-400 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400">Modèle</label>
                    <input
                      type="text"
                      value={vllmModel}
                      onChange={(e) => setVllmModel(e.target.value)}
                      placeholder="meta-llama/Llama-3.1-8B-Instruct"
                      className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder-zinc-400 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400">
                      Clé API {hasVllmApiKey && <span className="text-emerald-500">(configurée ✓)</span>}
                    </label>
                    <input
                      type="password"
                      value={vllmApiKey}
                      onChange={(e) => setVllmApiKey(e.target.value)}
                      placeholder={hasVllmApiKey ? '••••••••' : 'Optionnel'}
                      className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder-zinc-400 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white dark:placeholder-zinc-500"
                    />
                  </div>
                </div>
              )}

              {/* Save button */}
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={saveEngineConfig}
                  disabled={engineSaving}
                  className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 disabled:opacity-50 dark:bg-violet-500 dark:hover:bg-violet-600"
                >
                  {engineSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
                {engineSaved && (
                  <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">✓ Sauvegardé</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section: Indexation intelligente */}
        <div className="mt-8 rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50/80 to-white/80 p-6 shadow-lg dark:border-amber-900/50 dark:from-amber-950/30 dark:to-zinc-950/80">
          <div className="flex items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/40">
                <svg className="h-6 w-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-zinc-900 dark:text-white">Indexation intelligente (IA)</h3>
                <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                  Lorsqu'activée, les nouveaux contenus sont automatiquement segmentés par l'IA locale en sections thématiques enrichies (titre, résumé, concepts, timestamps). Améliore significativement la qualité des réponses RAG.
                </p>
                <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500">
                  Modèle utilisé : {inferenceEngine === 'vllm' ? 'vLLM distant' : 'Ollama local'} · Temps d'indexation légèrement plus long
                </p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={smartIndexing}
              disabled={smartIndexingLoading || smartIndexingSaving}
              onClick={() => toggleSmartIndexing(!smartIndexing)}
              className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 disabled:opacity-50 ${
                smartIndexing ? 'bg-amber-500' : 'bg-zinc-300 dark:bg-zinc-600'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ${
                  smartIndexing ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
